import type { CSSProperties, KeyboardEventHandler, ReactNode } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Input } from 'antd';
import type { GetRef } from 'antd';
import { TbCircleXFilled, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface SearchFilterInputProps {
    ariaLabel?: string;
    bordered?: boolean;
    disabled?: boolean;
    focusOnMount?: boolean;
    iconColor?: CSSProperties['color'];
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    placeholder?: string;
    setValue: (value: string) => void;
    size?: 'small' | 'middle' | 'large' | undefined;
    suffix?: ReactNode;
    value: string;
}

type InputRef = GetRef<typeof Input>;

const SearchFilterInput = forwardRef<InputRef, SearchFilterInputProps>(
    (
        {
            ariaLabel,
            focusOnMount,
            value,
            setValue,
            placeholder,
            bordered,
            size,
            suffix,
            disabled,
            iconColor,
            onKeyDown,
        },
        ref,
    ): JSX.Element => {
        const { token } = useAntDesignTheme();
        const inputRef = useRef<InputRef>(null);

        useImperativeHandle(ref, () => inputRef.current as InputRef);

        useEffect(() => {
            if (focusOnMount) {
                inputRef.current?.focus();
            }
        }, [focusOnMount]);

        return (
            <Input
                ref={inputRef}
                allowClear={{
                    clearIcon: <TbCircleXFilled size='1rem' style={{ color: iconColor, verticalAlign: '-0.25rem' }} />,
                }}
                aria-label={ariaLabel ?? placeholder}
                bordered={bordered}
                disabled={disabled}
                size={size}
                prefix={<TbSearch style={{ color: iconColor ?? token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                suffix={suffix}
                placeholder={placeholder}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={onKeyDown}
            />
        );
    },
);

SearchFilterInput.displayName = 'SearchFilterInput';

export default SearchFilterInput;
